/**
 * ImportClientGuide Component
 *
 * Guides users through importing their first client from their connected integration.
 * Includes step-by-step instructions and opens the extension to the client creation page.
 */

import React, { useState } from "react";
import {
  Button,
  Card,
  Text,
  Timeline,
  Alert,
  Stack,
  Box,
  Title,
  Group,
} from "@mantine/core";
import {
  IconUserPlus,
  IconPin,
  IconCheck,
  IconArrowLeft,
} from "@tabler/icons-react";
import { openExtensionToClientCreation } from "@/utils/extensionClient";
import type { ImportClientMetadata } from "../types";

interface ImportClientGuideProps {
  metadata?: ImportClientMetadata;
  onComplete?: () => void;
}

const ImportClientGuide: React.FC<ImportClientGuideProps> = ({
  metadata,
  onComplete,
}) => {
  const [extensionOpened, setExtensionOpened] = useState(false);
  const integrationName = metadata?.integrationName || "your integration";
  const hasClients = metadata?.hasClients || false;

  // Capitalize integration name
  const formattedIntegrationName = integrationName
    .split(" ")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");

  const handleOpenExtension = async () => {
    const success = await openExtensionToClientCreation();
    if (success) {
      setExtensionOpened(true);
    }
  };

  const handleBack = () => {
    if (onComplete) {
      onComplete();
    }
  };

  return (
    <Box p="xl">
      <Stack gap="lg">
        {/* Header */}
        <Group justify="space-between">
          <Button
            variant="subtle"
            leftSection={<IconArrowLeft size={16} />}
            onClick={handleBack}
          >
            Back to Home
          </Button>
        </Group>

        <Card shadow="sm" padding="xl" radius="md" withBorder>
          <Stack gap="xl">
            {/* Title Section */}
            <Box>
              <Title order={2}>
                Import Your First Client from {formattedIntegrationName}
              </Title>
              <Text color="dimmed" size="md" mt="xs">
                Follow these steps to import client data into SmartWiz
              </Text>
            </Box>

            {/* Main Action Button */}
            <Button
              size="lg"
              onClick={handleOpenExtension}
              leftSection={<IconUserPlus size={20} />}
              fullWidth
              color="green"
            >
              Open Extension to Import Client
            </Button>

            {/* Timeline Steps */}
            <Timeline active={2} bulletSize={24} lineWidth={2}>
              <Timeline.Item
                bullet={<IconPin size={16} />}
                title="Step 1: Pin Your Main Form"
              >
                <Box
                  mt="xs"
                  p="md"
                  style={{
                    backgroundColor: "#f8f9fa",
                    borderRadius: "4px",
                    border: "1px dashed #dee2e6",
                  }}
                >
                  <Text size="sm" c="dimmed" fs="italic">
                    [Screenshot: How to pin a form will be added here]
                  </Text>
                </Box>
                <Text mt="md" size="sm">
                  In the extension, click the <strong>pin icon</strong> next to
                  your main client intake form. Pinned forms appear at the top
                  for easy access.
                </Text>
                <Text mt="xs" size="xs" color="dimmed">
                  <strong>Why?</strong> Pinning your main intake form makes it
                  faster to find and import clients.
                </Text>
              </Timeline.Item>

              <Timeline.Item
                bullet={<IconUserPlus size={16} />}
                title="Step 2: Select Clients to Import"
              >
                <Box
                  mt="xs"
                  p="md"
                  style={{
                    backgroundColor: "#f8f9fa",
                    borderRadius: "4px",
                    border: "1px dashed #dee2e6",
                  }}
                >
                  <Text size="sm" c="dimmed" fs="italic">
                    [Screenshot: Selecting form submissions will be added here]
                  </Text>
                </Box>
                <Text mt="md" size="sm">
                  Select the form submission you want to import, review the
                  client information extracted from the form, and click{" "}
                  <strong>Import</strong> to create the client in SmartWiz.
                </Text>
                <Text mt="xs" size="xs" c="blue" fw={500}>
                  💡 Tip: You can import multiple submissions at once!
                </Text>
              </Timeline.Item>

              <Timeline.Item
                bullet={<IconCheck size={16} />}
                title="Step 3: Success!"
              >
                {hasClients ? (
                  <Alert
                    color="green"
                    icon={<IconCheck size={20} />}
                    title="Completed!"
                    mt="xs"
                  >
                    <Text size="sm">
                      Great job! You've imported your first client 🎉
                    </Text>
                    <Text size="xs" mt="xs" color="dimmed">
                      You can now continue with the next onboarding steps.
                    </Text>
                  </Alert>
                ) : (
                  <Alert color="blue" variant="light" mt="xs">
                    <Text size="sm" color="dimmed">
                      Once you import your first client, this step will be
                      marked complete automatically.
                    </Text>
                  </Alert>
                )}
              </Timeline.Item>
            </Timeline>

            {/* Extension Opened Feedback */}
            {extensionOpened && !hasClients && (
              <Alert color="blue" icon={<IconUserPlus size={20} />}>
                <Text size="sm" fw={500}>
                  Extension opened!
                </Text>
                <Text size="sm" mt="xs">
                  Import a client to complete this step. This page will
                  auto-update when you return.
                </Text>
              </Alert>
            )}

            {/* Help Text */}
            <Box
              p="md"
              style={{
                backgroundColor: "#f8f9fa",
                borderRadius: "8px",
              }}
            >
              <Text size="sm" fw={500} mb="xs">
                Need help?
              </Text>
              <Text size="sm" c="dimmed">
                If you're having trouble importing clients, check that your{" "}
                {formattedIntegrationName} integration is properly connected and
                that you have form submissions available to import.
              </Text>
            </Box>
          </Stack>
        </Card>
      </Stack>
    </Box>
  );
};

export default ImportClientGuide;
